import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Shortcuts share the theme's sole quantity input and its stock/rule validation.
 * @typedef {object} Refs
 * @property {HTMLFieldSetElement} fieldset
 * @property {HTMLScriptElement} variantData
 * @extends {Component<Refs>}
 */
class OrbivanaQuantityOptions extends Component {
  requiredRefs = ['fieldset', 'variantData'];
  #controller;
  #observer;
  #frame;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.closest('form')?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#schedule, { signal });
    this.closest('form')?.addEventListener('change', this.#schedule, { signal });
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, this.#productSelect, { signal });
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(this.closest('form'), { subtree: true, childList: true, attributes: true, attributeFilter: ['min', 'max', 'step', 'disabled', 'data-cart-quantity'] });
    // Pumper mounts asynchronously beside the form. Script presence alone is insufficient.
    this.#observer.observe(this.closest('.shopify-section') || this.parentElement, { childList: true, subtree: true });
    customElements.whenDefined('quantity-selector-component').then(() => {
      if (!signal.aborted) this.#sync();
    });
  }

  updatedCallback() { super.updatedCallback(); this.#schedule(); }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#generation++;
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #productSelect = async (event) => {
    const generation = ++this.#generation;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      const incoming = [...(detail?.html?.querySelectorAll('orbivana-quantity-options') || [])]
        .find(node => node.dataset.formId === this.dataset.formId);
      if (incoming) this.refs.variantData.textContent = incoming.querySelector('[ref="variantData"]').textContent;
      this.#schedule();
    } catch { /* The native product form owns variant/network errors. */ }
  };

  /** @param {Event} event Radio changes update the existing native quantity control. */
  selectQuantity(event) {
    const radio = event.target;
    const selector = this.closest('form')?.querySelector('quantity-selector-component');
    if (!(radio instanceof HTMLInputElement) || radio.disabled || !selector) return;
    selector.setValue(radio.value);
    selector.onQuantityChange();
    selector.updateButtonStates();
    selector.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#sync();
  }

  #sync() {
    if (!this.isConnected) return;
    const form = this.closest('form');
    const selector = form?.querySelector('quantity-selector-component');
    const input = selector?.querySelector('input[name="quantity"]');
    if (!input || !selector.getValue) return;
    const app = this.closest('.shopify-section')?.querySelector('#pumper_bundle_svelte');
    const appReady = !!app && app.children.length > 0 && app.getBoundingClientRect().height > 0;
    this.hidden = appReady;
    // Pumper owns the submitted quantity; its native input may intentionally stay at 1.
    // Showing the independent +/- control would contradict the selected app tier.
    selector.closest('.quantity-selector-wrapper')?.toggleAttribute('data-ov-app-managed', appReady);
    if (this.refs.fieldset.disabled !== appReady) this.refs.fieldset.disabled = appReady;
    const variantId = form.querySelector('input[name="id"]')?.value;
    const variant = JSON.parse(this.refs.variantData.textContent).variants[variantId];
    const min = Number(input.min || 1), step = Number(input.step || 1);
    const max = input.max === '' ? Infinity : Number(input.max) - Number(input.dataset.cartQuantity || 0);
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      const quantity = Number(radio.value);
      const disabled = input.disabled || !variant?.available || quantity < min || quantity > max || (quantity - min) % step !== 0;
      if (radio.disabled !== disabled) radio.disabled = disabled;
      radio.checked = !radio.disabled && quantity === Number(input.value);
      const price = radio.closest('label')?.querySelector('[data-quantity-total]');
      if (variant && price && price.innerHTML !== variant.totals[quantity - 1]) price.innerHTML = variant.totals[quantity - 1];
    }
  }
}

if (!customElements.get('orbivana-quantity-options')) customElements.define('orbivana-quantity-options', OrbivanaQuantityOptions);
